import React from 'react';
import { motion } from 'framer-motion';
import { playClickSound } from '../utils/speech';

interface Props { age: 'little' | 'big'; onSelect: (section: string) => void; onBack: () => void; }

const sections = [
  { id: 'alphabet', title: 'Alphabet', arabic: 'الحروف', emoji: '🔤', color: '#FF6B6B', desc: 'Learn Arabic letters!' },
  { id: 'numbers', title: 'Numbers', arabic: 'الأرقام', emoji: '🔢', color: '#4ECDC4', desc: 'Count in Arabic!' },
  { id: 'colors', title: 'Colors', arabic: 'الألوان', emoji: '🎨', color: '#FF9F43', desc: 'Learn all the colors!' },
  { id: 'animals', title: 'Animals', arabic: 'الحيوانات', emoji: '🦁', color: '#95E1D3', desc: 'Meet animal friends!' },
  { id: 'greetings', title: 'Greetings', arabic: 'التحيات', emoji: '👋', color: '#A8DADC', desc: 'Say hello in Arabic!' },
  { id: 'matching', title: 'Match it!', arabic: 'صل الكلمات', emoji: '🎮', color: '#FFE66D', desc: 'Match words to pictures!' },
  { id: 'memory', title: 'Memory', arabic: 'لعبة الذاكرة', emoji: '🧠', color: '#6C63FF', desc: 'Find the pairs!' },
  { id: 'quiz', title: 'Quiz!', arabic: 'اختبار', emoji: '🎯', color: '#FD79A8', desc: 'Test your skills!' },
];

const littleSections = ['alphabet', 'numbers', 'colors', 'animals', 'matching', 'memory'];

export const Dashboard: React.FC<Props> = ({ age, onSelect, onBack }) => {
  const available = age === 'little' ? sections.filter(s => littleSections.includes(s.id)) : sections;

  return (
    <div style={{ minHeight: '100vh', padding: '20px' }}>
      <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px' }}>
        <motion.button whileTap={{ scale: 0.9 }} onClick={() => { playClickSound(); onBack(); }}
          style={{ background: 'white', border: '3px solid #FF6B6B', borderRadius: '50px', padding: '10px 20px', cursor: 'pointer', fontSize: '1rem', color: '#FF6B6B', fontWeight: 700 }}>
          ← Back
        </motion.button>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', color: 'var(--primary)' }}>
            <img src="/malek.jpg" alt="Mālik" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '2px solid #FF6B6B', verticalAlign: 'middle', marginRight: '8px' }} />
            Learn with Mālik
          </h1>
          <p style={{ color: '#636E72', fontSize: '0.9rem' }}>
            {age === 'little' ? '🐣 Little Explorer Mode' : '🦅 Big Learner Mode'}
          </p>
        </div>
        <div style={{ width: '80px' }} />
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '20px', maxWidth: '900px', margin: '0 auto' }}>
        {available.map((s, i) => (
          <motion.div key={s.id} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: i * 0.08, type: 'spring' }}
            whileHover={{ scale: 1.06, y: -8 }} whileTap={{ scale: 0.96 }}
            onClick={() => { playClickSound(); onSelect(s.id); }}
            style={{
              background: 'white', borderRadius: '24px', padding: '25px 15px',
              textAlign: 'center', cursor: 'pointer',
              boxShadow: `0 8px 30px ${s.color}30`, border: `3px solid ${s.color}40`
            }}>
            <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
              style={{ fontSize: '55px', marginBottom: '10px' }}>{s.emoji}</motion.div>
            <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: '1.2rem', color: s.color }}>{s.title}</div>
            <div style={{ fontSize: '1rem', color: '#2D3436', marginTop: '4px' }}>{s.arabic}</div>
            <div style={{ fontSize: '0.8rem', color: '#636E72', marginTop: '6px' }}>{s.desc}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
